/**
 * Chart.js global config + رنگ palette آنالیتیکس
 */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const PALETTE = {
  primary: '#A84B6A',
  primaryLight: 'rgba(178, 83, 115, 0.15)',
  success: '#2e7d32',
  successLight: 'rgba(46, 125, 50, 0.15)',
  warning: '#ef6c00',
  warningLight: 'rgba(239, 108, 0, 0.15)',
  danger: '#c62828',
  dangerLight: 'rgba(198, 40, 40, 0.15)',
  purple: '#7b1fa2',
  purpleLight: 'rgba(123, 31, 162, 0.15)',
  teal: '#00838f',
  tealLight: 'rgba(0, 131, 143, 0.15)',
  pink: '#c2185b',
  pinkLight: 'rgba(194, 24, 91, 0.15)',
  amber: '#f9a825',
  amberLight: 'rgba(249, 168, 37, 0.15)',
};

export const COLORS = [
  PALETTE.primary,
  PALETTE.success,
  PALETTE.warning,
  PALETTE.danger,
  PALETTE.purple,
  PALETTE.teal,
  PALETTE.pink,
  PALETTE.amber,
];

/**
 * Theme-aware chart chrome.
 * Chart.js draws to <canvas>, so it can't inherit CSS. We read the app's design
 * tokens from :root / [data-theme="dark"] and push them into Chart.js global
 * defaults (tick/legend text = --color-text-secondary, grid lines =
 * --color-divider). A MutationObserver re-applies them + repaints every live
 * chart when the theme toggles, so dark mode is exact at runtime.
 */
const readVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export function applyChartTheme(): void {
  if (typeof document === 'undefined') return;
  const text = readVar('--color-text-secondary', '#6b7280');
  const grid = readVar('--color-divider', 'rgba(0,0,0,0.08)');
  ChartJS.defaults.color = text;
  ChartJS.defaults.borderColor = grid;
  // Repaint already-mounted charts so a theme toggle updates them immediately.
  const instances = (ChartJS as any).instances || {};
  Object.keys(instances).forEach((id) => {
    try { instances[id].update('none'); } catch { /* chart disposed */ }
  });
}

if (typeof document !== 'undefined') {
  applyChartTheme();
  // React to runtime theme switches (ThemeContext flips <html data-theme>).
  new MutationObserver(applyChartTheme).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
}

export const defaultLineOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' as const, rtl: true, textDirection: 'rtl' },
    tooltip: { rtl: true, textDirection: 'rtl' },
  },
  scales: {
    x: { grid: { display: true } },
    y: { beginAtZero: true },
  },
};

export const defaultBarOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  plugins: {
    legend: { display: false },
    tooltip: { rtl: true, textDirection: 'rtl' },
  },
  scales: {
    x: { beginAtZero: true },
    y: { grid: { display: false } },
  },
};

export const defaultDoughnutOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'right' as const, rtl: true, textDirection: 'rtl' },
    tooltip: { rtl: true, textDirection: 'rtl' },
  },
};
